/**
 * TelemetryTab - Displays usage analytics for microapps (IDP-939)
 *
 * Shows:
 * - Header with app name and date range picker
 * - Active filter chips
 * - Metric cards with comparison stats (page loads, unique visitors, avg load time, avg session)
 * - Page load trend area chart with smooth animations
 * - Top pages table with visit bars
 * - Top locations table with horizontal bars
 * - Recent visitors table (Visitors section)
 */

import { default as React } from 'react';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  useApi,
  configApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Popover from '@mui/material/Popover';
import Link from '@mui/material/Link';
import Fade from '@mui/material/Fade';
import Grow from '@mui/material/Grow';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';

interface TelemetryStats {
  pageLoads: number;
  uniqueVisitors: number;
  avgLoadTimeMs: number;
  avgSessionDurationS: number;
  pageLoadsPrevPeriod?: number;
  uniqueVisitorsPrevPeriod?: number;
  avgLoadTimeMsPrevPeriod?: number;
}

interface DailyTrend {
  date: string;
  pageLoads: number;
}

interface TopPage {
  pageUrl: string;
  visitCount: number;
  uniqueVisitors?: number;
}

interface RecentVisitor {
  userEmail: string;
  visitCount: number;
  lastSeen: string;
}

interface TopLocation {
  location: string;
  visits: number;
  avgLoadTimeMs: number;
  userCount?: number;
}

interface PaginatedTopPages {
  pages: TopPage[];
  totalCount: number;
}

interface PaginatedVisitors {
  visitors: RecentVisitor[];
  totalCount: number;
}

interface PageVisitor {
  userEmail: string;
  visitCount: number;
  lastSeen: string;
}

interface PageVisitorsResponse {
  pageUrl: string;
  visitors: PageVisitor[];
  totalCount: number;
}

interface TelemetryResponse {
  appName: string;
  periodDays: number;
  stats: TelemetryStats;
  trend: DailyTrend[];
  topPages: PaginatedTopPages;
  topLocations: TopLocation[];
  recentVisitors: PaginatedVisitors;
}

const PRIMARY_BLUE = '#1565c0';
const LIGHT_BLUE = '#e3f2fd';

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (minutes < 60) return `${minutes}m ${secs}s`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}m`;
}

function formatLoadTime(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatNumber(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString();
}

function formatShortDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatDateTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function calculatePercentChange(
  current: number,
  previous: number | undefined,
): { value: number; isPositive: boolean } | null {
  if (previous === undefined || previous === 0) return null;
  const change = ((current - previous) / previous) * 100;
  return { value: Math.abs(Math.round(change)), isPositive: change >= 0 };
}

interface MetricCardProps {
  label: string;
  value: string | number;
  subtitle?: string;
  change?: { value: number; isPositive: boolean } | null;
  invertChangeColor?: boolean;
  delay?: number;
}

const MetricCard = ({
  label,
  value,
  subtitle,
  change,
  invertChangeColor,
  delay = 0,
}: MetricCardProps) => {
  const showPositiveAsGood = invertChangeColor
    ? !change?.isPositive
    : change?.isPositive;

  return (
    <Grow
      in
      timeout={400}
      style={{ transformOrigin: '0 0', transitionDelay: `${delay}ms` }}
    >
      <Paper
        variant="outlined"
        sx={{
          height: '100%',
          minHeight: 120,
          p: 2.5,
          borderLeft: `4px solid ${PRIMARY_BLUE}`,
          borderRadius: 2,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            transform: 'translateY(-2px)',
          },
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'text.secondary',
            display: 'block',
            mb: 1,
            fontSize: '0.7rem',
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            lineHeight: 1.1,
            mb: 0.5,
            color: 'text.primary',
          }}
        >
          {value}
        </Typography>
        {change && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
            {showPositiveAsGood ? (
              <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
            ) : (
              <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main' }} />
            )}
            <Typography
              variant="caption"
              sx={{
                color: showPositiveAsGood ? 'success.main' : 'error.main',
                fontWeight: 600,
                fontSize: '0.75rem',
              }}
            >
              {change.isPositive ? '↑' : '↓'} {change.value}% vs prev period
            </Typography>
          </Box>
        )}
        {subtitle && !change && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: 'block' }}
          >
            {subtitle}
          </Typography>
        )}
      </Paper>
    </Grow>
  );
};

interface TimeRangeSelectorProps {
  days: number;
  onDaysChange: (days: number) => void;
}

const TIME_RANGE_OPTIONS = [
  { value: 7, label: 'Last 7 days' },
  { value: 14, label: 'Last 14 days' },
  { value: 28, label: 'Last 28 days' },
  { value: 90, label: 'Last 90 days' },
];

const TimeRangeSelector = ({ days, onDaysChange }: TimeRangeSelectorProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (value: number) => {
    onDaysChange(value);
    handleClose();
  };

  const open = Boolean(anchorEl);
  const currentLabel =
    TIME_RANGE_OPTIONS.find(opt => opt.value === days)?.label ||
    `Last ${days} days`;

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<CalendarTodayIcon sx={{ fontSize: 18 }} />}
        onClick={handleClick}
        sx={{
          textTransform: 'none',
          borderColor: 'divider',
          color: 'text.primary',
          px: 2,
          py: 1,
          borderRadius: 2,
          fontWeight: 500,
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: PRIMARY_BLUE,
            bgcolor: LIGHT_BLUE,
          },
        }}
      >
        {currentLabel}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        TransitionComponent={Fade}
        sx={{ '& .MuiPaper-root': { borderRadius: 2, mt: 1 } }}
      >
        <Box sx={{ p: 1.5, minWidth: 160 }}>
          {TIME_RANGE_OPTIONS.map(option => (
            <Box
              key={option.value}
              onClick={() => handleSelect(option.value)}
              sx={{
                px: 2,
                py: 1.5,
                cursor: 'pointer',
                borderRadius: 1.5,
                fontWeight: days === option.value ? 600 : 400,
                bgcolor: days === option.value ? LIGHT_BLUE : 'transparent',
                color: days === option.value ? PRIMARY_BLUE : 'text.primary',
                transition: 'all 0.15s ease',
                '&:hover': {
                  bgcolor: days === option.value ? LIGHT_BLUE : 'action.hover',
                },
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 'inherit' }}>
                {option.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  );
};

interface LocationBarProps {
  location: string;
  users: number;
  avgLoadTime: string;
  percentage: number;
  isSelected: boolean;
  onClick: () => void;
}

const LocationBar = ({
  location,
  users,
  avgLoadTime,
  percentage,
  isSelected,
  onClick,
}: LocationBarProps) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      py: 1.5,
      px: 1.5,
      cursor: 'pointer',
      borderRadius: 1.5,
      bgcolor: isSelected ? LIGHT_BLUE : 'transparent',
      transition: 'all 0.2s ease',
      '&:hover': {
        bgcolor: isSelected ? LIGHT_BLUE : 'action.hover',
        transform: 'translateX(4px)',
      },
    }}
  >
    <Typography
      variant="body2"
      sx={{
        width: 140,
        flexShrink: 0,
        fontWeight: isSelected ? 600 : 400,
        color: isSelected ? PRIMARY_BLUE : 'text.primary',
        transition: 'all 0.2s ease',
      }}
    >
      {location}
    </Typography>
    <Box sx={{ flex: 1, mx: 2 }}>
      <Box
        sx={{
          height: 8,
          bgcolor: '#e0e0e0',
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            height: '100%',
            bgcolor: isSelected ? PRIMARY_BLUE : '#90caf9',
            borderRadius: 4,
            width: `${percentage}%`,
            minWidth: 4,
            transition: 'width 0.5s ease-out, background-color 0.2s ease',
          }}
        />
      </Box>
    </Box>
    <Typography
      variant="body2"
      sx={{ width: 50, textAlign: 'right', fontWeight: 500 }}
    >
      {users}
    </Typography>
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ width: 60, textAlign: 'right' }}
    >
      {avgLoadTime}
    </Typography>
  </Box>
);

export const TelemetryTab = () => {
  const { entity } = useEntity();
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);

  const [days, setDays] = useState(28);
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [telemetry, setTelemetry] = useState<TelemetryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination state for Top Pages
  const [pagesPage, setPagesPage] = useState(0);
  const [pagesRowsPerPage, setPagesRowsPerPage] = useState(25);
  const [topPagesData, setTopPagesData] = useState<PaginatedTopPages | null>(
    null,
  );
  const [topPagesLoading, setTopPagesLoading] = useState(false);

  // Pagination state for Visitors
  const [visitorsPage, setVisitorsPage] = useState(0);
  const [visitorsRowsPerPage, setVisitorsRowsPerPage] = useState(25);
  const [visitorsData, setVisitorsData] = useState<PaginatedVisitors | null>(
    null,
  );
  const [visitorsLoading, setVisitorsLoading] = useState(false);

  // Expandable row state for Top Pages - shows visitors per page
  const [expandedPageUrl, setExpandedPageUrl] = useState<string | null>(null);
  const [pageVisitors, setPageVisitors] = useState<PageVisitorsResponse | null>(
    null,
  );
  const [pageVisitorsLoading, setPageVisitorsLoading] = useState(false);
  const [pageVisitorsError, setPageVisitorsError] = useState<string | null>(
    null,
  );

  const appName = entity.metadata.name;

  const handleDaysChange = (newDays: number) => {
    setDays(newDays);
  };

  const activeFilters = useMemo(() => {
    const filters: { key: string; label: string }[] = [];
    filters.push({ key: 'app', label: `App: ${appName}` });
    if (selectedLocation) {
      filters.push({ key: 'location', label: `Location: ${selectedLocation}` });
    }
    return filters;
  }, [appName, selectedLocation]);

  const removeFilter = (key: string) => {
    if (key === 'location') {
      setSelectedLocation(null);
    }
  };

  const fetchTelemetry = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const backendUrl = configApi.getString('backend.baseUrl');
      const apiUrl = `${backendUrl}/api/telemetry/${encodeURIComponent(
        appName,
      )}/overview?days=${days}`;
      const { token } = await identityApi.getCredentials();

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch telemetry (${response.status})`);
      }

      const result = await response.json();
      setTelemetry(result);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [appName, days, configApi, identityApi]);

  // Fetch paginated top pages
  const fetchTopPages = useCallback(
    async (limit: number, offset: number) => {
      setTopPagesLoading(true);
      try {
        const backendUrl = configApi.getString('backend.baseUrl');
        const apiUrl = `${backendUrl}/api/telemetry/${encodeURIComponent(
          appName,
        )}/top-pages?days=${days}&limit=${limit}&offset=${offset}`;
        const { token } = await identityApi.getCredentials();

        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });

        if (!response.ok) {
          // Non-OK response — keep stale data, don't overwrite
          return;
        }
        const result = await response.json();
        setTopPagesData(result);
      } finally {
        setTopPagesLoading(false);
      }
    },
    [appName, days, configApi, identityApi],
  );

  // Fetch paginated visitors
  const fetchVisitors = useCallback(
    async (limit: number, offset: number) => {
      setVisitorsLoading(true);
      try {
        const backendUrl = configApi.getString('backend.baseUrl');
        const apiUrl = `${backendUrl}/api/telemetry/${encodeURIComponent(
          appName,
        )}/visitors?days=${days}&limit=${limit}&offset=${offset}`;
        const { token } = await identityApi.getCredentials();

        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });

        if (!response.ok) {
          // Non-OK response — keep stale data, don't overwrite
          return;
        }
        const result = await response.json();
        setVisitorsData(result);
      } finally {
        setVisitorsLoading(false);
      }
    },
    [appName, days, configApi, identityApi],
  );

  // Fetch visitors for a specific page (expandable row)
  const fetchPageVisitors = useCallback(
    async (pageUrl: string) => {
      setPageVisitorsLoading(true);
      setPageVisitorsError(null);
      try {
        const backendUrl = configApi.getString('backend.baseUrl');
        const apiUrl = `${backendUrl}/api/telemetry/${encodeURIComponent(
          appName,
        )}/page-visitors?days=${days}&pageUrl=${encodeURIComponent(
          pageUrl,
        )}&limit=10`;
        const { token } = await identityApi.getCredentials();

        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });

        if (!response.ok) {
          setPageVisitorsError(`Failed to fetch visitors (${response.status})`);
          return;
        }
        const result = await response.json();
        setPageVisitors(result);
      } catch (err) {
        setPageVisitorsError('Failed to fetch visitor data');
      } finally {
        setPageVisitorsLoading(false);
      }
    },
    [appName, days, configApi, identityApi],
  );

  // Handle row expand/collapse
  const handleRowExpand = useCallback(
    (pageUrl: string) => {
      if (expandedPageUrl === pageUrl) {
        // Collapse if already expanded
        setExpandedPageUrl(null);
        setPageVisitors(null);
        setPageVisitorsError(null);
      } else {
        // Expand and fetch visitors - clear stale data immediately to avoid race condition
        setExpandedPageUrl(pageUrl);
        setPageVisitors(null);
        setPageVisitorsError(null);
        fetchPageVisitors(pageUrl);
      }
    },
    [expandedPageUrl, fetchPageVisitors],
  );

  useEffect(() => {
    fetchTelemetry();
  }, [fetchTelemetry]);

  // Sync local pagination data with initial telemetry response and reset page indices
  useEffect(() => {
    if (telemetry) {
      setTopPagesData(telemetry.topPages);
      setVisitorsData(telemetry.recentVisitors);
      setPagesPage(0);
      setVisitorsPage(0);
      setExpandedPageUrl(null);
      setPageVisitors(null);
      setPageVisitorsError(null);
    }
  }, [telemetry]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400,
        }}
      >
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (error) {
    return (
      <Fade in>
        <Alert severity="error" sx={{ m: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      </Fade>
    );
  }

  if (!telemetry) {
    return (
      <Fade in>
        <Alert severity="info" sx={{ m: 2, borderRadius: 2 }}>
          No telemetry data available for this app.
        </Alert>
      </Fade>
    );
  }

  const { stats, trend, topLocations } = telemetry;

  // Use local paginated data or fall back to initial telemetry
  const topPages = topPagesData?.pages || telemetry.topPages.pages;
  const topPagesTotalCount =
    topPagesData?.totalCount || telemetry.topPages.totalCount;
  const recentVisitors =
    visitorsData?.visitors || telemetry.recentVisitors.visitors;
  const visitorsTotalCount =
    visitorsData?.totalCount || telemetry.recentVisitors.totalCount;

  const maxLocationVisits = Math.max(
    ...topLocations.map(l => l.userCount || l.visits),
    1,
  );

  const pageLoadsChange = calculatePercentChange(
    stats.pageLoads,
    stats.pageLoadsPrevPeriod,
  );
  const visitorsChange = calculatePercentChange(
    stats.uniqueVisitors,
    stats.uniqueVisitorsPrevPeriod,
  );
  const loadTimeChange = calculatePercentChange(
    stats.avgLoadTimeMs,
    stats.avgLoadTimeMsPrevPeriod,
  );

  const maxPageVisits = Math.max(...topPages.map(p => p.visitCount), 1);

  return (
    <Box sx={{ p: 0 }}>
      {/* Header Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#f8f9fa',
          borderBottom: '1px solid',
          borderColor: 'divider',
          borderRadius: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CalendarTodayIcon sx={{ color: PRIMARY_BLUE, fontSize: 22 }} />
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: 'text.primary' }}
          >
            Microapp Telemetry — {appName}
          </Typography>
        </Box>
        <TimeRangeSelector days={days} onDaysChange={handleDaysChange} />
      </Paper>

      <Box sx={{ px: 3 }}>
        {/* Active Filters */}
        <Fade in timeout={300}>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              mb: 3,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
              Active filters:
            </Typography>
            {activeFilters.map(filter => (
              <Chip
                key={filter.key}
                label={filter.label}
                size="small"
                onDelete={
                  filter.key !== 'app'
                    ? () => removeFilter(filter.key)
                    : undefined
                }
                sx={{
                  bgcolor: LIGHT_BLUE,
                  color: PRIMARY_BLUE,
                  fontWeight: 500,
                  borderRadius: 2,
                  transition: 'all 0.2s ease',
                  '& .MuiChip-deleteIcon': {
                    color: PRIMARY_BLUE,
                    '&:hover': { color: '#0d47a1' },
                  },
                  '&:hover': { bgcolor: '#bbdefb' },
                }}
              />
            ))}
          </Box>
        </Fade>

        {/* Metric Cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)',
            },
            gap: 3,
            mb: 4,
          }}
        >
          <MetricCard
            label="Page Loads"
            value={formatNumber(stats.pageLoads)}
            change={pageLoadsChange}
            delay={0}
          />
          <MetricCard
            label="Unique Visitors"
            value={formatNumber(stats.uniqueVisitors)}
            change={visitorsChange}
            delay={100}
          />
          <MetricCard
            label="Avg Load Time"
            value={formatLoadTime(stats.avgLoadTimeMs)}
            change={loadTimeChange}
            invertChangeColor
            subtitle="Server response"
            delay={200}
          />
          <MetricCard
            label="Avg Session"
            value={formatDuration(stats.avgSessionDurationS)}
            subtitle="Per visit"
            delay={300}
          />
        </Box>

        {/* Page Loads Over Time Chart */}
        <Fade in timeout={500}>
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              mb: 4,
              borderRadius: 2,
              transition: 'box-shadow 0.2s ease',
              '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.06)' },
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
              Page Loads Over Time
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 2, display: 'block' }}
            >
              Daily, last {days} days
              {selectedLocation && ` — filtered to ${selectedLocation}`}
            </Typography>
            {trend.length > 0 ? (
              <Box sx={{ height: 240, mt: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trend}>
                    <defs>
                      <linearGradient
                        id="colorPageLoads"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor={PRIMARY_BLUE}
                          stopOpacity={0.2}
                        />
                        <stop
                          offset="95%"
                          stopColor={PRIMARY_BLUE}
                          stopOpacity={0.02}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#eee"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatShortDate}
                      tick={{ fontSize: 11, fill: '#888' }}
                      axisLine={{ stroke: '#eee' }}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: '#888' }}
                      axisLine={false}
                      tickLine={false}
                      width={40}
                    />
                    <Tooltip
                      labelFormatter={d => formatShortDate(d as string)}
                      formatter={value => [
                        Number(value).toLocaleString(),
                        'Page Loads',
                      ]}
                      contentStyle={{
                        borderRadius: 8,
                        border: 'none',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                        padding: '8px 12px',
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="pageLoads"
                      stroke={PRIMARY_BLUE}
                      strokeWidth={2.5}
                      fill="url(#colorPageLoads)"
                      animationDuration={1000}
                      animationEasing="ease-out"
                      dot={{ r: 3, fill: PRIMARY_BLUE, strokeWidth: 0 }}
                      activeDot={{
                        r: 5,
                        fill: PRIMARY_BLUE,
                        strokeWidth: 2,
                        stroke: '#fff',
                      }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No trend data available
                </Typography>
              </Box>
            )}
          </Paper>
        </Fade>

        {/* Top Pages and Visitors - Equal Height Grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: 3,
            mb: 4,
          }}
        >
          <Fade in timeout={600}>
            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                borderRadius: 2,
                height: '100%',
                minHeight: 320,
                display: 'flex',
                flexDirection: 'column',
                transition: 'box-shadow 0.2s ease',
                '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.06)' },
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Top Pages
              </Typography>
              {topPages.length > 0 ? (
                <>
                  <TableContainer sx={{ flex: 1, position: 'relative' }}>
                    {topPagesLoading && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          bgcolor: 'rgba(255,255,255,0.7)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 1,
                        }}
                      >
                        <CircularProgress size={24} />
                      </Box>
                    )}
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{
                              width: 40,
                              borderBottom: '2px solid #eee',
                              py: 1.5,
                            }}
                          />
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: 'text.secondary',
                              fontSize: 11,
                              letterSpacing: '0.05em',
                              borderBottom: '2px solid #eee',
                              py: 1.5,
                            }}
                          >
                            PAGE
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 600,
                              color: 'text.secondary',
                              fontSize: 11,
                              letterSpacing: '0.05em',
                              borderBottom: '2px solid #eee',
                              py: 1.5,
                            }}
                          >
                            LOADS
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {topPages.map((page, idx) => {
                          const isExpanded = expandedPageUrl === page.pageUrl;
                          return (
                            <React.Fragment key={idx}>
                              <TableRow
                                sx={{
                                  transition: 'background-color 0.15s ease',
                                  '&:hover': { bgcolor: 'action.hover' },
                                  cursor: 'pointer',
                                  ...(isExpanded && {
                                    bgcolor: LIGHT_BLUE,
                                  }),
                                }}
                                onClick={() => handleRowExpand(page.pageUrl)}
                              >
                                <TableCell sx={{ py: 1, width: 40 }}>
                                  <IconButton
                                    size="small"
                                    onClick={e => {
                                      e.stopPropagation();
                                      handleRowExpand(page.pageUrl);
                                    }}
                                  >
                                    {isExpanded ? (
                                      <KeyboardArrowUpIcon fontSize="small" />
                                    ) : (
                                      <KeyboardArrowDownIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </TableCell>
                                <TableCell sx={{ py: 1.5 }}>
                                  <Link
                                    href="#"
                                    underline="hover"
                                    sx={{
                                      color: PRIMARY_BLUE,
                                      fontWeight: 500,
                                    }}
                                    onClick={e => e.preventDefault()}
                                  >
                                    {page.pageUrl}
                                  </Link>
                                </TableCell>
                                <TableCell align="right" sx={{ py: 1.5 }}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'flex-end',
                                      gap: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 500 }}
                                    >
                                      {page.visitCount.toLocaleString()}
                                    </Typography>
                                    <Box
                                      sx={{
                                        width: 50,
                                        height: 6,
                                        bgcolor: '#e3f2fd',
                                        borderRadius: 3,
                                        overflow: 'hidden',
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: `${
                                            (page.visitCount / maxPageVisits) *
                                            100
                                          }%`,
                                          height: '100%',
                                          bgcolor: PRIMARY_BLUE,
                                          borderRadius: 3,
                                          transition: 'width 0.4s ease-out',
                                        }}
                                      />
                                    </Box>
                                  </Box>
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell
                                  sx={{ py: 0, borderBottom: 'none' }}
                                  colSpan={3}
                                >
                                  <Collapse
                                    in={isExpanded}
                                    timeout="auto"
                                    unmountOnExit
                                  >
                                    <Box
                                      sx={{
                                        py: 2,
                                        px: 2,
                                        bgcolor: '#fafafa',
                                        borderRadius: 1,
                                        my: 1,
                                      }}
                                    >
                                      <Typography
                                        variant="subtitle2"
                                        sx={{ fontWeight: 600, mb: 1.5 }}
                                      >
                                        Visitors to this page
                                      </Typography>
                                      {pageVisitorsLoading && (
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            py: 2,
                                          }}
                                        >
                                          <CircularProgress size={20} />
                                        </Box>
                                      )}
                                      {!pageVisitorsLoading &&
                                        pageVisitors &&
                                        pageVisitors.visitors.length > 0 && (
                                          <Table size="small">
                                            <TableHead>
                                              <TableRow>
                                                <TableCell
                                                  sx={{
                                                    fontWeight: 600,
                                                    fontSize: 10,
                                                    color: 'text.secondary',
                                                    py: 1,
                                                  }}
                                                >
                                                  USER
                                                </TableCell>
                                                <TableCell
                                                  align="right"
                                                  sx={{
                                                    fontWeight: 600,
                                                    fontSize: 10,
                                                    color: 'text.secondary',
                                                    py: 1,
                                                  }}
                                                >
                                                  VISITS
                                                </TableCell>
                                                <TableCell
                                                  align="right"
                                                  sx={{
                                                    fontWeight: 600,
                                                    fontSize: 10,
                                                    color: 'text.secondary',
                                                    py: 1,
                                                  }}
                                                >
                                                  LAST SEEN
                                                </TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {pageVisitors.visitors.map(
                                                (visitor, vIdx) => (
                                                  <TableRow key={vIdx}>
                                                    <TableCell sx={{ py: 1 }}>
                                                      <Typography
                                                        variant="body2"
                                                        sx={{
                                                          color: PRIMARY_BLUE,
                                                          fontWeight: 500,
                                                        }}
                                                      >
                                                        {visitor.userEmail}
                                                      </Typography>
                                                    </TableCell>
                                                    <TableCell
                                                      align="right"
                                                      sx={{ py: 1 }}
                                                    >
                                                      {visitor.visitCount}
                                                    </TableCell>
                                                    <TableCell
                                                      align="right"
                                                      sx={{
                                                        py: 1,
                                                        color: 'text.secondary',
                                                      }}
                                                    >
                                                      {formatDateTime(
                                                        visitor.lastSeen,
                                                      )}
                                                    </TableCell>
                                                  </TableRow>
                                                ),
                                              )}
                                            </TableBody>
                                          </Table>
                                        )}
                                      {!pageVisitorsLoading &&
                                        pageVisitorsError && (
                                          <Alert
                                            severity="error"
                                            sx={{ borderRadius: 1 }}
                                          >
                                            {pageVisitorsError}
                                          </Alert>
                                        )}
                                      {!pageVisitorsLoading &&
                                        !pageVisitorsError &&
                                        (!pageVisitors ||
                                          pageVisitors.visitors.length ===
                                            0) && (
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                          >
                                            No visitor data available
                                          </Typography>
                                        )}
                                      {pageVisitors &&
                                        pageVisitors.totalCount > 10 && (
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ mt: 1, display: 'block' }}
                                          >
                                            Showing top 10 of{' '}
                                            {pageVisitors.totalCount} visitors
                                          </Typography>
                                        )}
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {topPagesTotalCount > pagesRowsPerPage && (
                    <TablePagination
                      component="div"
                      count={topPagesTotalCount}
                      page={pagesPage}
                      onPageChange={(_, newPage) => {
                        setPagesPage(newPage);
                        fetchTopPages(
                          pagesRowsPerPage,
                          newPage * pagesRowsPerPage,
                        );
                      }}
                      rowsPerPage={pagesRowsPerPage}
                      onRowsPerPageChange={e => {
                        const newRowsPerPage = parseInt(e.target.value, 10);
                        setPagesRowsPerPage(newRowsPerPage);
                        setPagesPage(0);
                        fetchTopPages(newRowsPerPage, 0);
                      }}
                      rowsPerPageOptions={[5, 10, 25]}
                      sx={{
                        borderTop: '1px solid #eee',
                        '& .MuiTablePagination-toolbar': { minHeight: 40 },
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
                          {
                            fontSize: 12,
                          },
                      }}
                    />
                  )}
                </>
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography color="text.secondary">
                    No page data available
                  </Typography>
                </Box>
              )}
            </Paper>
          </Fade>
          <Fade in timeout={700}>
            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                borderRadius: 2,
                height: '100%',
                minHeight: 320,
                display: 'flex',
                flexDirection: 'column',
                transition: 'box-shadow 0.2s ease',
                '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.06)' },
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Visitors
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 2, display: 'block' }}
              >
                {selectedLocation
                  ? `Filtered to ${selectedLocation} — ${stats.uniqueVisitors} users`
                  : `${stats.uniqueVisitors} users`}
              </Typography>
              {recentVisitors.length > 0 ? (
                <>
                  <TableContainer sx={{ flex: 1, position: 'relative' }}>
                    {visitorsLoading && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          bgcolor: 'rgba(255,255,255,0.7)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 1,
                        }}
                      >
                        <CircularProgress size={24} />
                      </Box>
                    )}
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: 'text.secondary',
                              fontSize: 11,
                              letterSpacing: '0.05em',
                              borderBottom: '2px solid #eee',
                              py: 1.5,
                            }}
                          >
                            USER
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 600,
                              color: 'text.secondary',
                              fontSize: 11,
                              letterSpacing: '0.05em',
                              borderBottom: '2px solid #eee',
                              py: 1.5,
                            }}
                          >
                            VISITS
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 600,
                              color: 'text.secondary',
                              fontSize: 11,
                              letterSpacing: '0.05em',
                              borderBottom: '2px solid #eee',
                              py: 1.5,
                            }}
                          >
                            LAST SEEN
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {recentVisitors.map((visitor, idx) => (
                          <TableRow
                            key={idx}
                            sx={{
                              transition: 'background-color 0.15s ease',
                              '&:hover': { bgcolor: 'action.hover' },
                            }}
                          >
                            <TableCell sx={{ py: 1.5 }}>
                              <Link
                                href="#"
                                underline="hover"
                                sx={{ color: PRIMARY_BLUE, fontWeight: 500 }}
                              >
                                {visitor.userEmail}
                              </Link>
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ py: 1.5, fontWeight: 500 }}
                            >
                              {visitor.visitCount.toLocaleString()}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ py: 1.5, color: 'text.secondary' }}
                            >
                              {formatDateTime(visitor.lastSeen)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {visitorsTotalCount > visitorsRowsPerPage && (
                    <TablePagination
                      component="div"
                      count={visitorsTotalCount}
                      page={visitorsPage}
                      onPageChange={(_, newPage) => {
                        setVisitorsPage(newPage);
                        fetchVisitors(
                          visitorsRowsPerPage,
                          newPage * visitorsRowsPerPage,
                        );
                      }}
                      rowsPerPage={visitorsRowsPerPage}
                      onRowsPerPageChange={e => {
                        const newRowsPerPage = parseInt(e.target.value, 10);
                        setVisitorsRowsPerPage(newRowsPerPage);
                        setVisitorsPage(0);
                        fetchVisitors(newRowsPerPage, 0);
                      }}
                      rowsPerPageOptions={[5, 10, 25]}
                      sx={{
                        borderTop: '1px solid #eee',
                        '& .MuiTablePagination-toolbar': { minHeight: 40 },
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
                          {
                            fontSize: 12,
                          },
                      }}
                    />
                  )}
                </>
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography color="text.secondary">
                    No visitor data available
                  </Typography>
                </Box>
              )}
            </Paper>
          </Fade>
        </Box>

        {/* Top Locations */}
        <Fade in timeout={800}>
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              borderRadius: 2,
              mb: 3,
              transition: 'box-shadow 0.2s ease',
              '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.06)' },
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Top Locations
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 2, display: 'block' }}
            >
              Click a location to filter all charts
              {selectedLocation && ` — ${selectedLocation} currently selected`}
            </Typography>
            {topLocations.length > 0 ? (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    py: 1,
                    px: 1.5,
                    mb: 0.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      width: 140,
                      fontWeight: 600,
                      color: 'text.secondary',
                      letterSpacing: '0.05em',
                    }}
                  >
                    CITY / REGION
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <Typography
                    variant="caption"
                    sx={{
                      width: 50,
                      textAlign: 'right',
                      fontWeight: 600,
                      color: 'text.secondary',
                      letterSpacing: '0.05em',
                    }}
                  >
                    USERS
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      width: 60,
                      textAlign: 'right',
                      fontWeight: 600,
                      color: 'text.secondary',
                      letterSpacing: '0.05em',
                    }}
                  >
                    AVG LOAD
                  </Typography>
                </Box>
                {topLocations.slice(0, 5).map((loc, idx) => (
                  <LocationBar
                    key={idx}
                    location={loc.location}
                    users={loc.userCount || loc.visits}
                    avgLoadTime={formatLoadTime(loc.avgLoadTimeMs)}
                    percentage={
                      ((loc.userCount || loc.visits) / maxLocationVisits) * 100
                    }
                    isSelected={selectedLocation === loc.location}
                    onClick={() =>
                      setSelectedLocation(
                        selectedLocation === loc.location ? null : loc.location,
                      )
                    }
                  />
                ))}
              </>
            ) : (
              <Box
                sx={{
                  py: 5,
                  textAlign: 'center',
                  bgcolor: '#fafafa',
                  borderRadius: 2,
                }}
              >
                <Typography color="text.secondary" variant="body2">
                  No location data available
                </Typography>
              </Box>
            )}
            {selectedLocation && (
              <Fade in>
                <Alert
                  severity="info"
                  icon={false}
                  sx={{
                    mt: 2,
                    bgcolor: '#fff8e1',
                    color: '#e65100',
                    borderRadius: 2,
                    '& .MuiAlert-message': { width: '100%' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">
                      ⚡ Cross-filter active — {selectedLocation} selected. All
                      charts reflect {selectedLocation} visitors only.{' '}
                      <Link
                        component="button"
                        onClick={() => setSelectedLocation(null)}
                        sx={{
                          color: '#e65100',
                          textDecoration: 'underline',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        Click again to clear.
                      </Link>
                    </Typography>
                  </Box>
                </Alert>
              </Fade>
            )}
          </Paper>
        </Fade>
      </Box>
    </Box>
  );
};
